import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, InputNumber, Select, Row, Col, Divider } from 'antd';
import type { InputNumberRef } from 'rc-input-number';
import styles from './DiceRollerModal.module.css';
import { useStableCallback } from '../../hooks/hookUtils';

const { Option } = Select;

interface DiceRollerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DiceRollerModal: React.FC<DiceRollerModalProps> = ({ isOpen, onClose }) => {
  const [numDice, setNumDice] = useState(1);
  const [dieSides, setDieSides] = useState(20);
  const [modifier, setModifier] = useState(0);
  const [result, setResult] = useState('');

  // InputNumber ref exposes .focus() which is all we need
  const numDiceInputRef = useRef<InputNumberRef>(null);

  // Memoized reset function to avoid creating a new function on every render.
  // This is particularly important since it's called from useEffect.
  const resetState = useCallback(() => {
    setNumDice(1);
    setDieSides(20);
    setModifier(0);
    setResult('');
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetState();
      // Focus the input shortly after the modal opens to ensure it's visible.
      setTimeout(() => {
        numDiceInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, resetState]);

  // Stable callback for rolling dice - ensures the callback always has access to
  // the latest state values without causing re-renders of child components
  const rollDice = useStableCallback(() => {
    let total = 0;
    const rolls = [];
    for (let i = 0; i < numDice; i++) {
      const roll = Math.floor(Math.random() * dieSides) + 1;
      rolls.push(roll);
      total += roll;
    }
    total += modifier;

    const modifierString = modifier > 0 ? ` + ${modifier}` : (modifier < 0 ? ` - ${Math.abs(modifier)}` : '');
    const resultString = `<strong>Total: ${total}</strong> <small>(Rolls: ${(rolls ?? []).join(', ')}${modifierString})</small>`;
    setResult(resultString);
  });

  // Stable callback for key press handling
  const handleKeyPress = useStableCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      rollDice();
    }
  });

  return (
    <Modal
      title="Dice Roller"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button key="roll" type="primary" onClick={rollDice}>
          Roll
        </Button>,
      ]}
    >
      <Row gutter={16} align="middle" onKeyDown={handleKeyPress}>
        <Col>Number of Dice:</Col>
        <Col>
          <InputNumber ref={numDiceInputRef} min={1} max={100} value={numDice} onChange={(value) => setNumDice(value ?? 1)} />
        </Col>
      </Row>
      <Row gutter={16} align="middle" className={styles.row} onKeyDown={handleKeyPress}>
        <Col>Type of Dice:</Col>
        <Col>
          <Select value={dieSides} onChange={(value) => setDieSides(value)} className={styles.select}>
            <Option value={4}>d4</Option>
            <Option value={6}>d6</Option>
            <Option value={8}>d8</Option>
            <Option value={10}>d10</Option>
            <Option value={12}>d12</Option>
            <Option value={20}>d20</Option>
            <Option value={100}>d100</Option>
          </Select>
        </Col>
      </Row>
      <Row gutter={16} align="middle" className={styles.row} onKeyDown={handleKeyPress}>
        <Col>Modifier:</Col>
        <Col>
          <InputNumber value={modifier} onChange={(value) => setModifier(value ?? 0)} />
        </Col>
      </Row>
      <Divider />
      {result && (
        <div dangerouslySetInnerHTML={{ __html: result }} />
      )}
    </Modal>
  );
};

export default DiceRollerModal;